/**
 * ParcoursList — Refonte UI/UX niveau Landing PESACORE
 * Toutes les fonctionnalités d'origine sont conservées intactes.
 * Design system identique au LandingPesacore :
 *   tokens T, DM Mono + Georgia, grille causale, scan-lines,
 *   coins HUD, connexions SVG, panneau latéral, top/bottom rail.
 */

import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion';
import {
  Activity,
  ArrowLeft,
  Beaker,
  ChevronRight,
  Cpu,
  Database,
  Layers,
  Lock,
  Search,
  Terminal,
  X,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { parcoursService } from '../services/parcoursService';

/* ── DESIGN TOKENS (identiques au Landing) ──────────────────── */
const T = {
  bg:        '#050a0e',
  bgPanel:   '#080d12',
  bgCard:    '#0b1219',
  border:    '#1a2a38',
  accent:    '#e07b20',
  accentG:   '#f59e0b',
  cyan:      '#0ea5e9',
  cyanG:     '#38bdf8',
  green:     '#10b981',
  purple:    '#8b5cf6',
  red:       '#f87171',
  gold:      '#C9A55A',
  goldGlow:  'rgba(201,165,90,0.35)',
  text:      '#f7f7f7',
  textDim:   'rgba(250, 250, 250, 0.93)',
  textMute:  'rgba(255, 255, 255, 0.45)',
  scan:      'rgba(14,165,233,0.018)',
  // surfaces PESA sim
  surface0:  'rgba(5,10,14,0.97)',
  surface1:  'rgba(8,13,18,0.93)',
  surface2:  'rgba(11,18,25,0.85)',
  border0:   'rgba(26,42,56,0.7)',
  border1:   'rgba(26,42,56,1)',
};

/* ── HELPERS ─────────────────────────────────────────────────── */
const Mono = ({ children, size = 11, color = T.textMute, w = false, style = {} }) => (
  <span
    style={{
      fontFamily: "'DM Mono', 'Roboto Mono', monospace",
      fontSize: size,
      letterSpacing: '0.06em',
      color,
      fontWeight: w ? 700 : 400,
      lineHeight: 1,
      ...style,
    }}
  >
    {children}
  </span>
);

/* ── HORLOGE LIVE ────────────────────────────────────────────── */
const LiveClock = () => {
  const [time, setTime] = useState('');
  useEffect(() => {
    const tick = () =>
      setTime(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return <span>{time}</span>;
};

/* ── GRILLE CAUSALE (identique au Landing) ───────────────────── */
const CausalGrid = ({ activeColor }) => {
  const cols = 11, rows = 8;
  const nodes = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++)
      nodes.push({ id: `${r}-${c}`, x: (c / (cols - 1)) * 100, y: (r / (rows - 1)) * 100, delay: (r + c) * 0.06 });

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {Array.from({ length: rows }).map((_, r) => (
        <motion.div
          key={`h${r}`}
          animate={{ opacity: activeColor ? [0.04, 0.12, 0.04] : [0.015, 0.04, 0.015] }}
          transition={{ duration: 4 + r, repeat: Infinity, delay: r * 0.3 }}
          style={{
            position: 'absolute', left: 0, right: 0, top: `${(r / (rows - 1)) * 100}%`, height: 1,
            background: `linear-gradient(90deg,transparent,${activeColor || T.cyan}40,transparent)`,
          }}
        />
      ))}
      {Array.from({ length: cols }).map((_, c) => (
        <motion.div
          key={`v${c}`}
          animate={{ opacity: activeColor ? [0.04, 0.12, 0.04] : [0.015, 0.04, 0.015] }}
          transition={{ duration: 5 + c * 0.4, repeat: Infinity, delay: c * 0.25 }}
          style={{
            position: 'absolute', top: 0, bottom: 0, left: `${(c / (cols - 1)) * 100}%`, width: 1,
            background: `linear-gradient(180deg,transparent,${activeColor || T.accent}30,transparent)`,
          }}
        />
      ))}
      {nodes.map(n => (
        <motion.div
          key={n.id}
          animate={{
            opacity: activeColor ? [0.12, 0.55, 0.12] : [0.02, 0.14, 0.02],
            scale: activeColor ? [0.8, 1.4, 0.8] : [0.8, 1, 0.8],
          }}
          transition={{ duration: 3 + Math.random() * 2, repeat: Infinity, delay: n.delay }}
          style={{
            position: 'absolute', left: `${n.x}%`, top: `${n.y}%`,
            width: 3, height: 3, borderRadius: '50%',
            background: activeColor || T.cyan, transform: 'translate(-50%,-50%)',
          }}
        />
      ))}
    </div>
  );
};

/* ── SCAN LINE CARTE ─────────────────────────────────────────── */
const CardScanLine = ({ color = T.cyan }) => (
  <motion.div
    animate={{ top: ['-5%', '105%'], opacity: [0, 0.5, 0] }}
    transition={{ duration: 4.5, repeat: Infinity, ease: 'linear', repeatDelay: 3 }}
    style={{
      position: 'absolute', left: 0, right: 0, height: '1px', pointerEvents: 'none', zIndex: 20,
      background: `linear-gradient(90deg, transparent, ${color}55, ${T.accentG}40, transparent)`,
    }}
  />
);

/* ── COINS HUD ───────────────────────────────────────────────── */
const HudCorners = ({ color1 = T.cyan, color2 = T.accent, size = 8 }) => (
  <>
    <span style={{ position: 'absolute', top: -1, left: -1, width: size, height: size, borderTop: `1px solid ${color1}`, borderLeft: `1px solid ${color1}`, zIndex: 5 }} />
    <span style={{ position: 'absolute', bottom: -1, right: -1, width: size, height: size, borderBottom: `1px solid ${color2}`, borderRight: `1px solid ${color2}`, zIndex: 5 }} />
  </>
);

/* ── OVERLAY VERROUILLÉ ──────────────────────────────────────── */
const LockedOverlay = () => (
  <div
    style={{
      position: 'absolute', inset: 0, zIndex: 10,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(5,10,14,0.65)', backdropFilter: 'blur(3px)',
      pointerEvents: 'none',
    }}
  >
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem',
        padding: '1rem 1.25rem', border: `1px solid ${T.danger}28`,
        background: 'rgba(5,10,14,0.9)',
      }}
    >
      <Lock size={18} style={{ color: `${T.danger}90` }} strokeWidth={1.5} />
      <Mono size={9} color={`${T.danger}80`} style={{ letterSpacing: '0.25em', textTransform: 'uppercase' }}>
        Module verrouillé
      </Mono>
      <Mono size={8} style={{ letterSpacing: '0.1em', color: T.textMute, textAlign: 'center', maxWidth: 140, lineHeight: 1.6 }}>
        Terminez un Cas d'étude pour débloquer l'accès
      </Mono>
    </div>
  </div>
);

/* ── ÉCRAN DE CHARGEMENT ─────────────────────────────────────── */
const LoadingScreen = () => (
  <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: T.bg, gap: '2rem' }}>
    <div style={{ position: 'relative', width: 80, height: 80 }}>
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
        style={{
          position: 'absolute', inset: 0,
          border: `1px solid ${T.cyan}18`,
          borderTop: `1px solid ${T.cyan}`,
          borderRight: `1px solid ${T.accent}55`,
          borderRadius: '50%',
        }}
      />
      <Cpu size={26} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: T.cyan }} strokeWidth={1} />
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
      <Mono size={9} color={T.cyan} style={{ letterSpacing: '0.4em', textTransform: 'uppercase' }}>
        Chargement_Protocoles...
      </Mono>
      <div style={{ width: 180, height: 1, background: 'rgba(255,255,255,0.04)', position: 'relative', overflow: 'hidden' }}>
        <motion.div
          animate={{ x: ['-100%', '100%'] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          style={{ position: 'absolute', inset: 0, background: `linear-gradient(90deg, transparent, ${T.cyan}, ${T.accentG}, transparent)` }}
        />
      </div>
    </div>
  </div>
);

/* ── PANNEAU LATÉRAL PARCOURS ────────────────────────────────── */
const ParcoursSidePanel = ({ parcours, onClose, onNavigate, isLocked }) => {
  const color = T.accentG;
  const difficulte = parcours?.difficulte || 3;

  return (
    <motion.div
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: 'fixed', top: 0, bottom: 0, right: 0, zIndex: 500,
        width: 'min(420px,90vw)',
        display: 'flex', flexDirection: 'column',
        fontFamily: "'DM Mono', monospace",
        background: 'rgba(5,8,14,0.97)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        borderLeft: `1px solid rgba(255,255,255,0.07)`,
        boxShadow: `-8px 0 52px rgba(0,0,0,0.75), 0 0 0 1px ${color}12`,
        overflow: 'hidden',
      }}
    >
      {/* Ligne accent gauche */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 2, background: `linear-gradient(180deg, transparent, ${color}65, ${color}28, transparent)` }} />
      {/* Scan-lines */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: `repeating-linear-gradient(0deg,rgba(255,255,255,0.012) 0,rgba(255,255,255,0.012) 1px,transparent 1px,transparent 4px)` }} />

      {/* Header */}
      <div style={{ position: 'relative', zIndex: 10, padding: '1.2rem 1.4rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}
              style={{ width: 6, height: 6, borderRadius: '50%', background: color, boxShadow: `0 0 10px ${color}` }} />
            <Mono size={9} color={T.textMute} style={{ letterSpacing: '0.28em', textTransform: 'uppercase' }}>Module Actif</Mono>
          </div>
          <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} onClick={onClose}
            style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textMute, cursor: 'pointer' }}>
            <X size={12} />
          </motion.button>
        </div>

        {/* Image */}
        <div style={{ height: 120, borderRadius: 8, overflow: 'hidden', background: T.bgCard, marginBottom: '1rem', position: 'relative', border: `1px solid ${T.border}` }}>
          {parcours?.image ? (
            <img src={parcours.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.65 }} />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Database size={32} strokeWidth={1} style={{ color: `${T.cyan}22` }} />
            </div>
          )}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(5,10,14,0.8), transparent)' }} />
          <div style={{ position: 'absolute', bottom: '0.5rem', left: '0.7rem' }}>
            <Mono size={8} color={`${T.cyan}80`} style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>
              Ref: {parcours?.slug?.toUpperCase() || parcours?._id?.slice(-6).toUpperCase()}
            </Mono>
          </div>
        </div>

        <div style={{ fontSize: '1.05rem', fontFamily: "'Georgia', serif", fontWeight: 700, color: T.text, letterSpacing: '-0.01em', marginBottom: '0.3rem' }}>
          {parcours?.nom}
        </div>
        <Mono size={10} color="rgba(255,255,255,0.38)" style={{ lineHeight: 1.6 }}>
          {parcours?.description || 'En attente de documentation supplémentaire...'}
        </Mono>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1.2rem 1.4rem', display: 'flex', flexDirection: 'column', gap: '1.1rem', position: 'relative', zIndex: 10 }}>

        {/* Difficulté */}
        <div>
          <Mono size={9} color={T.textMute} style={{ letterSpacing: '0.22em', textTransform: 'uppercase', display: 'block', marginBottom: '0.55rem' }}>
            Load_Difficulty
          </Mono>
          <div style={{ display: 'flex', gap: 4 }}>
            {[1, 2, 3, 4, 5].map(v => (
              <div key={v} style={{
                flex: 1, height: 5, borderRadius: 2,
                background: v <= difficulte
                  ? `linear-gradient(90deg, ${T.cyan}, ${T.accentG})`
                  : 'rgba(255,255,255,0.06)',
              }} />
            ))}
          </div>
          <Mono size={9} color={color} style={{ display: 'block', marginTop: '0.35rem' }}>
            Niveau {difficulte}/5
          </Mono>
        </div>

        {/* Métriques */}
        <div>
          <Mono size={9} color={T.textMute} style={{ letterSpacing: '0.22em', textTransform: 'uppercase', display: 'block', marginBottom: '0.55rem' }}>
            Indicateurs_Système
          </Mono>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem' }}>
            {[
              { k: 'Récompense', v: `${parcours?.reward || 500} PTS`, color: T.accentG },
              { k: 'Secteur', v: parcours?.categories || 'FORM', color: T.cyan },
              { k: 'Statut', v: isLocked ? 'LOCKED' : 'OPEN', color: isLocked ? T.danger : T.green },
              { k: 'Version', v: 'v1.0', color: T.textMute },
            ].map((m, i) => (
              <div key={i} style={{ background: T.bgCard, borderRadius: 7, padding: '0.5rem 0.65rem', border: `1px solid ${T.border}` }}>
                <Mono size={8} color={T.textMute} style={{ display: 'block', marginBottom: '0.2rem' }}>{m.k}</Mono>
                <Mono size={12} color={m.color} w style={{ display: 'block' }}>{m.v}</Mono>
              </div>
            ))}
          </div>
        </div>

        {/* Barres d'activité simulées */}
        <div>
          <Mono size={9} color={T.textMute} style={{ letterSpacing: '0.22em', textTransform: 'uppercase', display: 'block', marginBottom: '0.55rem' }}>
            Flux_Signal
          </Mono>
          {[72, 55, 88].map((v, i) => (
            <div key={i} style={{ marginBottom: '0.45rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                <Mono size={8} color={T.textMute}>{['Engagement', 'Cohérence', 'Complétion'][i]}</Mono>
                <Mono size={8} color={color}>{v}%</Mono>
              </div>
              <div style={{ height: 2, borderRadius: 1, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${v}%` }}
                  transition={{ duration: 1.2, ease: 'easeOut', delay: i * 0.12 }}
                  style={{ height: '100%', borderRadius: 1, background: `linear-gradient(90deg, ${T.cyan}, ${color})` }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <div style={{ padding: '0.65rem 0.8rem', borderRadius: 8, border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.015)' }}>
          <Mono size={9} color={T.textMute} style={{ letterSpacing: '0.14em', textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem' }}>Architecture</Mono>
          <Mono size={9} color="rgba(255,255,255,0.38)" style={{ lineHeight: 1.6, display: 'block' }}>
            Chaque module s'instancie sur le moteur causal découplé. Aucune dépendance à un modèle tiers.
          </Mono>
        </div>
      </div>

      {/* Footer CTA */}
      <div style={{ position: 'relative', zIndex: 10, padding: '0.9rem 1.4rem', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', gap: '0.45rem', flexShrink: 0 }}>
        <motion.button
          onClick={() => !isLocked && onNavigate()}
          whileHover={!isLocked ? { boxShadow: `0 0 28px ${color}40` } : {}}
          whileTap={!isLocked ? { scale: 0.98 } : {}}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            padding: '0.85rem', borderRadius: 9,
            background: isLocked ? 'rgba(255,255,255,0.03)' : `linear-gradient(135deg, ${color}22, ${color}10)`,
            border: `1px solid ${isLocked ? 'rgba(255,255,255,0.06)' : color + '32'}`,
            color: isLocked ? T.textMute : color,
            fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            cursor: isLocked ? 'not-allowed' : 'pointer',
          }}
        >
          {isLocked ? <Lock size={11} /> : <Zap size={11} />}
          {isLocked ? 'Accès Verrouillé' : 'Détails_Parcours'}
        </motion.button>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Mono size={8} style={{ letterSpacing: '0.1em' }}>PESACORE · bêta</Mono>
          <Mono size={8} color={`${T.green}88`} style={{ letterSpacing: '0.1em' }}>● NOMINAL</Mono>
        </div>
      </div>
    </motion.div>
  );
};

/* ── CARTE PARCOURS (style ControlPanel du Landing) ──────────── */
const ParcoursCard = ({ parcours, idx, isActive, isDimmed, isLocked, onClick }) => {
  const color = T.accentG;
  const b1 = useRef(Math.floor(40 + Math.random() * 50)).current;
  const b2 = useRef(Math.floor(30 + Math.random() * 60)).current;
  const b3 = useRef(Math.floor(55 + Math.random() * 40)).current;
  const difficulte = parcours.difficulte || 3;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{
        opacity: isDimmed ? 0.28 : 1,
        scale: isActive ? 1.02 : 1,
      }}
      exit={{ opacity: 0, scale: 0.92 }}
      transition={{ duration: 0.3, delay: idx * 0.03, type: 'spring', stiffness: 200, damping: 22 }}
      onClick={onClick}
      style={{ cursor: isLocked ? 'not-allowed' : 'pointer', position: 'relative' }}
    >
      <motion.div
        animate={{
          borderColor: isActive ? color : T.border,
          boxShadow: isActive
            ? `0 0 0 1px ${color}55, 0 0 32px ${color}18, inset 0 0 28px ${color}04`
            : '0 0 0 1px transparent',
        }}
        transition={{ duration: 0.22 }}
        style={{
          background: isActive ? `linear-gradient(145deg, ${T.bgCard}, ${T.bgPanel})` : T.bgCard,
          border: `1px solid ${isActive ? color : T.border}`,
          borderRadius: 10, overflow: 'hidden', position: 'relative',
          fontFamily: "'DM Mono', monospace",
        }}
      >
        {/* Scan-lines fond */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`, pointerEvents: 'none', borderRadius: 10 }} />
        <CardScanLine color={isActive ? color : T.cyan} />
        <HudCorners color1={isActive ? color : `${T.cyan}55`} color2={isActive ? T.accent : `${T.accent}55`} size={7} />

        {/* Miniature */}
        <div style={{ height: 110, position: 'relative', overflow: 'hidden', background: '#070d12', flexShrink: 0 }}>
          {parcours.image ? (
            <img
              src={parcours.image} alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: isLocked ? 0.25 : 0.5, filter: isLocked ? 'grayscale(0.6)' : 'none', transition: 'opacity 0.4s' }}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Database size={30} strokeWidth={1} style={{ color: `${T.cyan}14` }} />
            </div>
          )}
          {isLocked && <LockedOverlay />}

          {/* Badge top-right */}
          <div style={{ position: 'absolute', top: '0.45rem', right: '0.45rem', display: 'flex', gap: '0.25rem', zIndex: 6 }}>
            <Mono size={7} color={`${T.cyan}70`} style={{
              padding: '0.18rem 0.45rem', background: 'rgba(5,10,14,0.8)', backdropFilter: 'blur(8px)',
              border: `1px solid rgba(255,255,255,0.08)`, letterSpacing: '0.2em', textTransform: 'uppercase',
            }}>
              SEC_L0{(idx % 4) + 1}
            </Mono>
            {isLocked && (
              <Mono size={7} color={`${T.danger}80`} style={{
                display: 'flex', alignItems: 'center', gap: 3, padding: '0.18rem 0.45rem',
                background: `${T.danger}12`, border: `1px solid ${T.danger}30`,
                letterSpacing: '0.2em', textTransform: 'uppercase',
              }}>
                <Lock size={7} /> LOCKED
              </Mono>
            )}
          </div>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent, ${T.cyan}28, ${T.accent}22, transparent)` }} />
        </div>

        {/* Corps carte */}
        <div style={{ padding: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>

          {/* Header : ref + indicateur */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Mono size={8} color={`${T.cyan}60`} style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>
              Ref: {parcours.slug?.toUpperCase() || parcours._id?.slice(-6).toUpperCase()}
            </Mono>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <div style={{ width: 4, height: 4, borderRadius: '50%', background: isActive ? color : '#1e3040', boxShadow: isActive ? `0 0 8px ${color}` : 'none' }} />
              <Mono size={8} color={isActive ? color : T.textMute}>{isActive ? '● ON' : '○ —'}</Mono>
            </div>
          </div>

          {/* Titre */}
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: isActive ? color : T.text, letterSpacing: '0.04em', textTransform: 'uppercase', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
            {parcours.nom}
          </div>

          {/* Barres d'activité */}
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            {[b1, b2, b3].map((v, i) => (
              <div key={i} style={{ flex: 1, height: 2, background: '#0c1620', borderRadius: 1, overflow: 'hidden' }}>
                <motion.div
                  animate={{ width: [`${v}%`, `${Math.min(v + 9, 100)}%`, `${v}%`] }}
                  transition={{ duration: 3 + i, repeat: Infinity, ease: 'easeInOut', delay: i * 0.55 }}
                  style={{ height: '100%', background: color, opacity: isActive ? 0.9 : 0.28, borderRadius: 1 }}
                />
              </div>
            ))}
          </div>

          {/* Description */}
          <Mono size={9} color="rgba(255,255,255,0.35)" style={{ lineHeight: 1.65, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {parcours.description || 'En attente de documentation supplémentaire...'}
          </Mono>

          {/* Stats : difficulté + reward */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.35rem' }}>
            <div style={{ background: '#070d13', borderRadius: 6, padding: '0.3rem 0.45rem', border: `1px solid ${T.border}` }}>
              <Mono size={7} color={T.textMute} style={{ display: 'block', marginBottom: '0.2rem' }}>Load_Difficulty</Mono>
              <div style={{ display: 'flex', gap: 2 }}>
                {[1, 2, 3, 4, 5].map(v => (
                  <div key={v} style={{ width: 10, height: 2, borderRadius: 1, background: v <= difficulte ? `linear-gradient(90deg, ${T.cyan}, ${color})` : 'rgba(255,255,255,0.06)' }} />
                ))}
              </div>
            </div>
            <div style={{ background: '#070d13', borderRadius: 6, padding: '0.3rem 0.45rem', border: `1px solid ${T.border}` }}>
              <Mono size={7} color={T.textMute} style={{ display: 'block', marginBottom: '0.2rem' }}>Rewards</Mono>
              <Mono size={13} color={color} w>{parcours.reward || '500'} PTS</Mono>
            </div>
          </div>

          {/* CTA */}
          <motion.div
            whileHover={!isLocked ? { boxShadow: `0 0 18px ${color}30` } : {}}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.65rem 0.85rem', borderRadius: 7,
              background: isLocked ? 'rgba(255,255,255,0.02)' : `${color}0d`,
              border: `1px solid ${isLocked ? 'rgba(255,255,255,0.04)' : color + '28'}`,
              color: isLocked ? T.textMute : color,
              transition: 'all 0.25s',
            }}
          >
            <Mono size={9} color={isLocked ? T.textMute : color} w style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}>
              {isLocked ? 'Accès_Verrouillé' : 'Démarrer le Module'}
            </Mono>
            {isLocked ? <Lock size={10} strokeWidth={1.5} style={{ color: T.textMute }} /> : <ChevronRight size={11} strokeWidth={1.5} />}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ── COMPOSANT PRINCIPAL ─────────────────────────────────────── */
const ParcoursList = () => {
  const navigate = useNavigate();
  const { user, fetchUser, loading: authLoading } = useAuth();

  const [parcoursList, setParcoursList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSector, setSelectedSector] = useState('ALL');
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [sidePanel, setSidePanel] = useState(null);
  const { scrollY } = useScroll();

  const userLoaded = user !== null && user !== undefined;
  const isParcoursUnlocked = authLoading ? null : (user?.unlockedAteliersCount ?? false);

  /* ── Chargement données (inchangé) ── */
  useEffect(() => {
    fetchUser?.();
    const handleFocus = () => fetchUser?.();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchUser]);

  useEffect(() => {
    const loadParcours = async () => {
      try {
        setLoading(true);
        const list = await parcoursService.list();
        setParcoursList(list);
      } catch {
        toast.error('Échec chargement parcours');
      } finally {
        setLoading(false);
      }
    };
    loadParcours();
  }, [user?.unlockedAteliersCount]);

  useMotionValueEvent(scrollY, 'change', y => setScrolled(y > 40));

  /* ── Filtrage (inchangé) ── */
  const filteredParcours = useMemo(() => {
    return parcoursList.filter(p => {
      const matchSearch = (p.nom + p.description).toLowerCase().includes(searchTerm.toLowerCase());
      const matchSector = selectedSector === 'ALL' || p.categories === selectedSector;
      return matchSearch && matchSector;
    });
  }, [parcoursList, searchTerm, selectedSector]);

  /* ── Navigation (logique inchangée) ── */
  const handleParcoursClick = useCallback((parcours) => {
    // Active la carte et ouvre le panneau latéral
    setActiveId(prev => {
      if (prev === parcours._id) {
        setSidePanel(null);
        return null;
      }
      setSidePanel(parcours);
      return parcours._id;
    });
  }, []);

  const handleNavigate = useCallback(() => {
    if (!sidePanel) return;
    if (isParcoursUnlocked === null) { navigate(`/parcours/${sidePanel.slug}`); return; }
    if (!isParcoursUnlocked) {
      toast(
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontFamily: 'monospace', fontSize: '0.65rem' }}>
          <Lock size={16} style={{ color: T.cyan, flexShrink: 0 }} />
          <span>Accès verrouillé — Complétez le <strong>Cas d'étude</strong> pour débloquer tous les modules</span>
        </div>,
        { duration: 5000, position: 'bottom-center' }
      );
      return;
    }
    navigate(`/parcours/${sidePanel.slug}`);
  }, [sidePanel, isParcoursUnlocked, navigate]);

  const closePanel = () => { setSidePanel(null); setActiveId(null); };

  if (loading) return <LoadingScreen />;

  const SECTORS = [
    { id: 'ALL', label: 'All_Protocols', icon: Layers },
    { id: 'TECH', label: 'Engineering', icon: Cpu },
    { id: 'SOFT_SKILLS', label: 'Human_Ops', icon: Activity },
    { id: 'LAB', label: 'Experimental', icon: Beaker },
  ];

  const isLocked = isParcoursUnlocked === null ? false : !isParcoursUnlocked;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.bg,
        color: T.text,
        position: 'relative',
        overflowX: 'hidden',
        fontFamily: "'DM Mono', monospace",
        paddingBottom: '5rem',
      }}
    >
      {/* ── Fond : grille causale ── */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <CausalGrid activeColor={activeId ? T.accentG : null} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(${T.border} 1px,transparent 1px),linear-gradient(90deg,${T.border} 1px,transparent 1px)`, backgroundSize: '52px 52px', opacity: 0.3 }} />
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 20% 0%, ${T.cyan}06 0%, transparent 50%), radial-gradient(ellipse at 80% 100%, ${T.accent}05 0%, transparent 50%)` }} />
      </div>

      {/* ── TOP RAIL (fixe) ── */}
      <motion.div
        animate={{ opacity: scrolled ? 0.96 : 1 }}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 110,
          background: `${T.bgPanel}d8`, backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <div style={{
          maxWidth: 1440, margin: '0 auto', padding: '0.55rem 2rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {/* Bouton retour */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: T.textMute, padding: 0, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = T.textDim}
            onMouseLeave={e => e.currentTarget.style.color = T.textMute}
          >
            <ArrowLeft size={13} />
            <Mono size={10} color="inherit">Dashboard</Mono>
          </button>

          {/* Centre : titre */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Terminal size={13} style={{ color: T.cyan }} strokeWidth={1.5} />
            <Mono size={9} color={T.textMute} style={{ letterSpacing: '0.3em', textTransform: 'uppercase' }}>
              Parcours.Engine
            </Mono>
          </div>

          {/* Droite : clock + status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
            <Mono size={9} color={`${T.accentG}80`}><LiveClock /></Mono>
            <span style={{ width: 1, height: 12, background: `${T.border}` }} />
            <Mono size={9} color={`${T.green}80`} style={{ letterSpacing: '0.2em' }}>● SYSTEM STABLE</Mono>
          </div>
        </div>
      </motion.div>

      {/* ── MAIN ── */}
      <main style={{ position: 'relative', zIndex: 10, maxWidth: 1600, margin: '0 auto', padding: '5rem 1.5rem 2rem' }}>

        {/* ── HEADER ── */}
        <header style={{ marginTop: 52, marginBottom: '2.5rem', paddingBottom: '2rem', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1.5rem' }}>

            {/* Titre display */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, border: `1px solid ${T.cyan}28`, background: `${T.cyan}06`, position: 'relative' }}>
                  <span style={{ position: 'absolute', top: -1, left: -1, width: 6, height: 6, borderTop: `1px solid ${T.cyan}`, borderLeft: `1px solid ${T.cyan}` }} />
                  <Terminal size={14} style={{ color: T.cyan }} strokeWidth={1.5} />
                </div>
                <div style={{ height: 1, width: 36, background: `linear-gradient(to right, ${T.cyan}50, transparent)` }} />
                <Mono size={8} color="rgba(255,255,255,0.2)" style={{ letterSpacing: '0.35em', textTransform: 'uppercase' }}>
                  Protocole System v1.0
                </Mono>
              </div>

              <h1 style={{ fontFamily: "'Georgia', serif", fontWeight: 300, fontSize: 'clamp(2.2rem,5vw,3.6rem)', letterSpacing: '-0.03em', lineHeight: 1, color: T.text, marginBottom: '0.25rem' }}>
                Parcours
                <span style={{ background: `linear-gradient(90deg, ${T.cyan}, ${T.accentG})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  .Engine
                </span>
              </h1>
              <Mono size={9} color="rgba(255,255,255,0.2)" style={{ letterSpacing: '0.28em', textTransform: 'uppercase' }}>
                PESACORE Architect · v1.0
              </Mono>
            </div>

            {/* Badges statut */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
              {[
                { label: 'Core: Stable', color: T.green },
                { label: `${parcoursList.length} Modules`, color: T.cyan },
                { label: 'Sync: 100%', color: T.accentG },
                { label: `User: ${user?.nom || 'Guest'}`, color: T.textMute },
              ].map(b => (
                <div key={b.label} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.25rem 0.65rem',
                  border: `1px solid ${b.color}22`, background: `${b.color}08`,
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: b.color, animation: 'dotpulse 2s ease-in-out infinite' }} />
                  <Mono size={8} color={`${b.color}90`} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>{b.label}</Mono>
                </div>
              ))}
            </div>
          </div>
        </header>

        {/* ── PANNEAU FILTRES ── */}
        <section
          style={{
            marginBottom: '2.5rem',
            background: `${T.bgPanel}90`, backdropFilter: 'blur(16px)',
            border: `1px solid ${T.border}`,
            padding: '1rem', position: 'relative',
            borderRadius: 4,
          }}
        >
          <HudCorners color1={T.cyan} color2={T.accent} size={7} />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.85rem', alignItems: 'center' }}>
            {/* Recherche */}
            <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 0 }}>
              <Search size={13} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: `${T.cyan}55`, pointerEvents: 'none' }} />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="RECHERCHER_DANS_LA_BASE..."
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}`, outline: 'none',
                  padding: '0.75rem 0.75rem 0.75rem 2.25rem',
                  fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: '0.18em',
                  color: T.text, caretColor: T.cyan, borderRadius: 4, transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = `${T.cyan}40`}
                onBlur={e => e.target.style.borderColor = T.border}
              />
            </div>

            {/* Filtres secteur */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {SECTORS.map(sector => {
                const active = selectedSector === sector.id;
                return (
                  <button
                    key={sector.id}
                    onClick={() => setSelectedSector(sector.id)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                      padding: '0.6rem 1rem', borderRadius: 4,
                      background: active ? `linear-gradient(135deg, ${T.accentG}, ${T.accent})` : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${active ? 'transparent' : T.border}`,
                      color: active ? T.bg : T.textMute,
                      fontFamily: "'DM Mono', monospace", fontSize: 9, fontWeight: active ? 700 : 400,
                      letterSpacing: '0.2em', textTransform: 'uppercase',
                      cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s',
                      boxShadow: active ? `0 0 18px ${T.accentG}28` : 'none',
                    }}
                    onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = `${T.cyan}30`; e.currentTarget.style.color = T.textDim; } }}
                    onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMute; } }}
                  >
                    <sector.icon size={11} strokeWidth={1.5} />
                    {sector.label}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── GRILLE DES CARTES ── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
            gap: '1rem',
          }}
        >
          <AnimatePresence mode="popLayout">
            {filteredParcours.map((p, idx) => (
              <ParcoursCard
                key={p._id}
                parcours={p}
                idx={idx}
                isActive={activeId === p._id}
                isDimmed={activeId !== null && activeId !== p._id}
                isLocked={isLocked}
                onClick={() => handleParcoursClick(p)}
              />
            ))}
          </AnimatePresence>
        </div>

        {/* ── ÉTAT VIDE ── */}
        {filteredParcours.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            style={{
              textAlign: 'center', padding: '5rem 2rem', marginTop: '2rem',
              border: `1px solid ${T.border}`, background: `${T.bgPanel}60`, position: 'relative',
              borderRadius: 4,
            }}
          >
            <HudCorners color1={`${T.cyan}40`} color2={`${T.accent}40`} size={10} />
            <div style={{ display: 'inline-flex', padding: '1rem', background: `${T.cyan}06`, border: `1px solid ${T.cyan}14`, marginBottom: '1.25rem', borderRadius: 8 }}>
              <Search size={28} strokeWidth={1} style={{ color: `${T.cyan}30` }} />
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: T.textDim, marginBottom: '0.5rem' }}>
              No_Match_Found
            </div>
            <Mono size={10} style={{ letterSpacing: '0.15em', color: T.textMute }}>
              La base de données ne renvoie aucun résultat pour cette requête.
            </Mono>
          </motion.div>
        )}
      </main>

      {/* ── PANNEAU LATÉRAL ── */}
      <AnimatePresence>
        {sidePanel && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closePanel}
              style={{ position: 'fixed', inset: 0, zIndex: 490, background: 'rgba(5,10,14,0.55)', backdropFilter: 'blur(2px)' }}
            />
            <ParcoursSidePanel
              parcours={sidePanel}
              onClose={closePanel}
              onNavigate={handleNavigate}
              isLocked={isLocked}
            />
          </>
        )}
      </AnimatePresence>

      {/* ── FOOTER RAIL FIXE ── */}
      <footer
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 60, height: 40,
          background: `${T.bgPanel}e8`, backdropFilter: 'blur(20px)',
          borderTop: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 1.5rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: T.green, boxShadow: `0 0 6px ${T.green}` }} />
            <Mono size={8} color={`${T.green}70`} style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>Node_Online</Mono>
          </div>
          <Mono size={8} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>
            Total_Modules: {parcoursList.length}
          </Mono>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
          <Mono size={8} style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>Latency: 24ms</Mono>
          {[['SIM', T.green], ['CAU', T.cyan], ['INV', T.accent]].map(([tag, col]) => (
            <div key={tag} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <div style={{ width: 4, height: 4, borderRadius: '50%', background: col, boxShadow: `0 0 5px ${col}` }} />
              <Mono size={8} style={{ letterSpacing: '0.14em' }}>{tag}</Mono>
            </div>
          ))}
          <div style={{
            padding: '0.18rem 0.65rem', background: `${T.cyan}0d`, border: `1px solid ${T.cyan}28`,
          }}>
            <Mono size={8} color={`${T.cyan}70`} style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}>System_Authenticated</Mono>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes dotpulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
      `}</style>
    </div>
  );
};

export default ParcoursList;